import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';
import useMeterTable from '../useMeterTable';

const DEMO_COLUMNS = ['Meter', 'Status', 'kWh'];
const DEMO_ROWS = [
    ['GT1_EMS1', 'Active', 0],
    ['GT2_EMS2', 'Active', 0],
    ['GT3_EMS3', 'Inactive', 0],
];

const STATUS_COLORS = { Active: '#0ca30c', Inactive: '#d03b3b', Warning: '#d69e00', Critical: '#b91c1c' };

const MODIFIER_CLASS = {
    basic: '',
    striped: 'table-widget--striped',
    bordered: 'table-widget--bordered',
    compact: 'table-widget--compact',
    'dark-header': 'table-widget--dark-header',
    minimal: 'table-widget--minimal',
    'hover-highlight': 'table-widget--hover',
};

const TableWidget = ({ config }) => {
    const { dataSource = {}, settings = {}, variant = 'basic' } = config;
    const [remoteData, setRemoteData] = useState(null);
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const isLiveTable = config.type === 'table';
    const live = useMeterTable({
        meterId: settings.meterId,
        skip: Boolean(dataSource.endpoint) || isPreview || !isLiveTable,
    });

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => { if (!cancelled) setRemoteData(res.data); })
            .catch((err) => console.error('TableWidget: failed to fetch', err));

        return () => { cancelled = true; };
    }, [dataSource.endpoint]);

    const columns = remoteData?.columns || (live.columns.length ? live.columns : null) || dataSource.columns || DEMO_COLUMNS;
    const rows = remoteData?.rows || (live.rows.length ? live.rows : null) || dataSource.rows || DEMO_ROWS;
    const accent = settings.color || '#4318FF';
    const headerTextColor = settings.textColor || accent;

    if (variant === 'card-rows') {
        return (
            <div className="table-widget table-widget--card-rows">
                {rows.map((row, i) => (
                    <div className="table-widget__card" key={i} style={{ borderLeftColor: accent }}>
                        {row.map((cell, j) => (
                            <div className="table-widget__card-cell" key={j}>
                                <span className="table-widget__card-label">{columns[j]}</span>
                                <span className="table-widget__card-value">{cell}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    if (variant === 'numbered') {
        return (
            <div className="table-widget">
                <table>
                    <thead>
                        <tr>
                            <th style={{ borderBottomColor: accent, color: headerTextColor }}>#</th>
                            {columns.map((col) => (
                                <th key={col} style={{ borderBottomColor: accent, color: headerTextColor }}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                <td className="table-widget__index">{i + 1}</td>
                                {row.map((cell, j) => <td key={j}>{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (variant === 'status-badges') {
        const statusIdx = columns.findIndex((c) => /status/i.test(c));
        return (
            <div className="table-widget">
                <table>
                    <thead>
                        <tr>{columns.map((col) => (
                            <th key={col} style={{ borderBottomColor: accent, color: headerTextColor }}>{col}</th>
                        ))}</tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => (
                                    j === statusIdx ? (
                                        <td key={j}>
                                            <span
                                                className="table-widget__badge"
                                                style={{ backgroundColor: `${STATUS_COLORS[cell] || '#9ca3af'}1A`, color: STATUS_COLORS[cell] || '#6b7280' }}
                                            >
                                                {cell}
                                            </span>
                                        </td>
                                    ) : <td key={j}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    const modifierClass = variant === 'custom'
        ? [
            settings.customStriped && 'table-widget--striped',
            settings.customBordered && 'table-widget--bordered',
            settings.customCompact && 'table-widget--compact',
            settings.customDarkHeader && 'table-widget--dark-header',
            settings.customHover && 'table-widget--hover',
        ].filter(Boolean).join(' ')
        : (MODIFIER_CLASS[variant] || '');

    return (
        <div className={`table-widget${modifierClass ? ` ${modifierClass}` : ''}`}>
            <table>
                <thead>
                    <tr>{columns.map((col) => (
                        <th key={col} style={{ borderBottomColor: settings.color || '#e5e7eb', color: settings.textColor || settings.color || '#6b7280' }}>{col}</th>
                    ))}</tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TableWidget;
